import * as Api from '@ViewModels';
import { IImageLibraryItem, ImageLibrarySource } from './types';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const IMAGE_LIBRARY_QUERY_KEY = 'imageLibrary';

const IMAGE_LIBRARY_PAGE_SIZE = 30;

/** What a listing should contain. Anything other than `image` has to be asked for by name. */
export type ImageLibraryKindFilter = 'image' | 'document' | 'all';

interface ImageLibraryQueryParams {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	/** Matched against the library name and the original file name */
	query?: string;
	source?: ImageLibrarySource;
	/**
	 * Defaults to images on the server as well, so a picker that omits it never receives a document it
	 * would try to render in an `img` tag.
	 */
	kind?: ImageLibraryKindFilter;
	/** Folder to filter on. Paging is server-side, so this cannot be applied to the fetched pages. */
	keyword?: string;
	/** Entries in no folder at all. Ignored when `keyword` is set. */
	unfiled?: boolean;
}

export function useImageLibraryQuery({
	enabled = true,
	impersonationContext,
	query,
	source,
	kind,
	keyword,
	unfiled,
}: ImageLibraryQueryParams) {
	const userSession = useUserSession();

	return useInfiniteQuery({
		enabled,
		meta: {
			errorLogging: { category: 'ImageLibrary', action: 'ImageLibraryQuery-Error' },
			toastErrorMessage: 'Failed to load your image library',
		},
		queryKey: [IMAGE_LIBRARY_QUERY_KEY, impersonationContext, query, source, kind, keyword, unfiled],
		queryFn: async ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<IImageLibraryItem>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'imageLibrary',
					queryParams: {
						pageToken: pageParam || null,
						pageSize: IMAGE_LIBRARY_PAGE_SIZE,
						query: query || null,
						source: source || null,
						...toKindQueryParams(kind),
						keyword: keyword || null,
						unfiled: unfiled || null,
					},
				}),
				'GET'
			);
		},
		// Coerce to null: react-query only stops on null/undefined, so an empty-string token would page forever.
		getNextPageParam: lastPage => lastPage.pageToken || null,
		initialPageParam: null as string | null,
	});
}

/**
 * The API takes a document kind plus an explicit everything flag rather than a third enum member, so that
 * a caller naming neither still gets images only.
 */
export function toKindQueryParams(kind: ImageLibraryKindFilter | undefined) {
	return { allKinds: kind === 'all' || null, kind: kind === 'document' ? 'Document' : null };
}

export function invalidateImageLibraryQuery() {
	return reactQueryClient.invalidateQueries({ queryKey: [IMAGE_LIBRARY_QUERY_KEY] });
}

/**
 * Adapts a library entry to the attachment shape every image picker consumer expects. The library entry's
 * own id identifies the entry, so the attachment id has to come from the nested snapshot.
 */
export function imageLibraryItemToAttachment(item: IImageLibraryItem): Api.IFileAttachment {
	return {
		fileName: item.fileAttachment.fileName,
		fileSize: item.fileAttachment.fileSize,
		id: item.fileAttachment.id,
		mimeType: item.mimeType,
		url: item.url,
	};
}

export function imageLibraryDisplayName(item: IImageLibraryItem) {
	return item.displayName || item.fileAttachment?.fileName;
}
